import React, { useCallback, useRef, useState } from "react";
import { Button, Divider, Form, Input, InputNumber, Select } from "antd";
import { inject, observer } from "mobx-react";
import { FormInstance } from "antd/lib/form/hooks/useForm";
import SelectClient from "./selectClient";
import SelectRegion from "./selectRegion";
import Calculator, {
  BELARUS_REGION,
  OtherRegionType,
  REGION_OPTIONS,
  RegionType,
} from "../../calculators/clientsFeeCalculator";
import { IAppStore } from "../../stores/Interfaces";

interface Props {
  store?: IAppStore;
}

const calculator = new Calculator();

const ClientsFeeCalculator: React.FC<Props> = ({ store }) => {
  const formRef = useRef<FormInstance>(null);

  const [calculateSum, setCalculateSum] = useState<React.ReactElement>();

  const onFinish = useCallback((values) => {
    calculator.calculate(values.sum);
  }, []);

  const onFieldsChanged = useCallback(() => {
    calculator.reset();
  }, []);

  const formTail = (
    <>
      <Form.Item
        label="Sum"
        name="sum"
        rules={[
          { required: true, message: "Please input sum!" },
          { type: "number", min: 0, message: "Please input positive sum!" },
        ]}
      >
        <InputNumber />
      </Form.Item>

      <Form.Item wrapperCol={{ offset: 8, span: 8 }}>
        <Button type="primary" htmlType="submit">
          Calculate
        </Button>
      </Form.Item>
    </>
  );

  const calculateSumForBelarus = (
    <>
      <Form.Item name="region_code" hidden initialValue={BELARUS_REGION}>
        <Input />
      </Form.Item>
      {formTail}
    </>
  );

  const onCustomsGroupChanged = useCallback((value: OtherRegionType) => {
    calculator.regionCode = value;
  }, []);

  const calculateSumForOthers = (
    <>
      <Form.Item
        label="Customs Groups"
        name="region_code"
        rules={[{ required: true, message: "Please select Customs Group!" }]}
      >
        <Select
          placeholder="Select Customs Group"
          options={REGION_OPTIONS}
          onChange={onCustomsGroupChanged}
        />
      </Form.Item>
      {formTail}
    </>
  );

  const showCalculateSumSection = useCallback((): void => {
    if (calculator.client && calculator.region)
      setCalculateSum(
        calculator.region === BELARUS_REGION
          ? calculateSumForBelarus
          : calculateSumForOthers
      );
  }, [calculateSumForBelarus, calculateSumForOthers]);

  const onClientChanged = useCallback(
    (value: string) => {
      calculator.client = value;
      showCalculateSumSection();
    },
    [showCalculateSumSection]
  );

  const onClientSearch = useCallback(
    (value: string) => {
      calculator.client = value;
      showCalculateSumSection();
    },
    [showCalculateSumSection]
  );

  const onRegionChanged = useCallback(
    (value: RegionType) => {
      calculator.region = value;

      if (value === BELARUS_REGION) calculator.regionCode = BELARUS_REGION;

      formRef?.current?.setFieldsValue({
        region_code: value === BELARUS_REGION ? value : null,
      });

      showCalculateSumSection();
    },
    [showCalculateSumSection]
  );

  return (
    <Form
      ref={formRef}
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 8 }}
      onFinish={onFinish}
      onFieldsChange={onFieldsChanged}
    >
      <Form.Item label="Client" name="client_id">
        <SelectClient
          placeholder="Select Client"
          onChange={onClientChanged}
          onSearch={onClientSearch}
          store={store}
        />
      </Form.Item>

      <Form.Item label="Region" name="region">
        <SelectRegion onChange={onRegionChanged} placeholder="Select Region" />
      </Form.Item>

      {calculateSum}

      <Divider />

      <Form.Item label="Result">
        <Input readOnly value={calculator.result as number} />
      </Form.Item>
    </Form>
  );
};

export default inject("store")(observer(ClientsFeeCalculator));
